import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, RefreshCw, Search, Pencil, Trash2, Eye, Check, X, MoreHorizontal, Users, ExternalLink, MessageSquare, Phone } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { clearTemplateCache } from "@/services/templateService";
import { fetchVoiceTemplateAgents, clearVoiceTemplateCache } from "@/services/voiceTemplateService";
// Note: Database types import may be unavailable
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

type TemplateAgent = {
    id: string;
    title: string | null;
    description: string | null;
    organization_id: string;
    is_template: boolean | null;
    is_active: boolean | null;
    created_at: string;
    industries?: {
        id: string;
        name: string;
        emoji: string | null;
    } | null;
    personas?: {
        id: string;
        avatar_name: string | null;
        type: string | null;
    } | null;
    organizations?: {
        id: string;
        name: string;
    } | null;
};

type VoiceTemplateAgent = {
    id: string;
    title: string;
    agent_name: string;
    description?: string | null;
    organization_id: string;
    created_by: string;
    instructions: string;
    first_message: string;
    is_template?: boolean | null;
    is_active?: boolean | null;
    persona_id?: string | null;
    industry_id?: string | null;
    created_at: string;
    industries?: {
        id: string;
        name: string;
        emoji: string | null;
    } | null;
    personas?: {
        id: string;
        avatar_name: string | null;
        type: string | null;
    } | null;
    organizations?: {
        id: string;
        name: string;
    } | null;
};

const TemplateManagerPage = () => {
    const [templates, setTemplates] = useState<TemplateAgent[]>([]);
    const [voiceTemplates, setVoiceTemplates] = useState<VoiceTemplateAgent[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<TemplateAgent[]>([]);
    const [filteredVoiceTemplates, setFilteredVoiceTemplates] = useState<VoiceTemplateAgent[]>([]);
    const [industries, setIndustries] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeAgentTab, setActiveAgentTab] = useState("chat");
    const { toast } = useToast();
    const { organization } = useAuth();
    const navigate = useNavigate();

    const fetchTemplates = async () => {
        try {
            // Fetch chat templates with proper joins
            const { data: chatTemplatesData, error: chatTemplatesError } = await supabase
                .from("agents")
                .select(`
                    *,
                    industries (id, name, emoji),
                    personas (id, avatar_name, type),
                    organizations (id, name)
                `)
                .eq("is_template", true)
                .order("created_at", { ascending: false });

            if (chatTemplatesError) {
                console.error("Error fetching chat templates:", chatTemplatesError);
                toast({
                    title: "Error",
                    description: "Failed to fetch chat templates",
                    variant: "destructive",
                });
                return;
            }

            // Fetch voice templates
            const voiceResult = await fetchVoiceTemplateAgents({ is_template: true });
            if (voiceResult && 'error' in voiceResult && voiceResult.error) {
                console.error("Error fetching voice templates:", voiceResult.error);
                toast({
                    title: "Error", 
                    description: "Failed to fetch voice templates",
                    variant: "destructive",
                });
                return;
            }

            // Fetch industries
            const { data: industriesData, error: industriesError } = await supabase
                .from("industries")
                .select("*")
                .order("name");

            if (industriesError) {
                console.error("Error fetching industries:", industriesError);
            }

            setTemplates(chatTemplatesData || []);
            setVoiceTemplates((voiceResult && 'data' in voiceResult ? voiceResult.data : []) as VoiceTemplateAgent[] || []);
            setIndustries(industriesData || []);
        } catch (error) {
            console.error("Error in fetchTemplates:", error);
            toast({
                title: "Error",
                description: "Failed to fetch templates",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const filterTemplates = () => {
        if (!searchTerm.trim()) {
            setFilteredTemplates(templates);
            setFilteredVoiceTemplates(voiceTemplates);
            return;
        }

        const searchLower = searchTerm.toLowerCase();
        
        // Filter chat templates
        const filteredChat = templates.filter((template) =>
            template.title?.toLowerCase().includes(searchLower) ||
            template.description?.toLowerCase().includes(searchLower) ||
            template.organizations?.name?.toLowerCase().includes(searchLower) ||
            template.personas?.avatar_name?.toLowerCase().includes(searchLower) ||
            template.industries?.name?.toLowerCase().includes(searchLower)
        );

        // Filter voice templates  
        const filteredVoice = voiceTemplates.filter((template) =>
            template.title?.toLowerCase().includes(searchLower) ||
            template.description?.toLowerCase().includes(searchLower) ||
            template.organizations?.name?.toLowerCase().includes(searchLower) ||
            template.personas?.avatar_name?.toLowerCase().includes(searchLower) ||
            template.industries?.name?.toLowerCase().includes(searchLower)
        );

        setFilteredTemplates(filteredChat);
        setFilteredVoiceTemplates(filteredVoice);
    };

    useEffect(() => {
        filterTemplates();
    }, [searchTerm, templates, voiceTemplates]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        clearTemplateCache();
        clearVoiceTemplateCache();
        await fetchTemplates();
        setIsRefreshing(false);
        toast({
            title: "Success",
            description: "Templates refreshed successfully",
        });
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const handleViewTemplate = (template: TemplateAgent) => {
        navigate(`/super-admin/templates/${template.id}`);
    };

    const handleViewVoiceTemplate = (template: VoiceTemplateAgent) => {
        navigate(`/super-admin/voice-templates/${template.id}`);
    };

    const handleEditTemplate = (template: TemplateAgent) => {
        navigate(`/super-admin/templates/${template.id}?edit=true`);
    };

    const handleEditVoiceTemplate = (template: VoiceTemplateAgent) => {
        navigate(`/super-admin/voice-templates/${template.id}?edit=true`);
    };

    const handleDeleteTemplate = (template: TemplateAgent) => {
        console.log("Delete template:", template.id);
        // TODO: Implement delete functionality
    };

    const handleDeleteVoiceTemplate = (template: VoiceTemplateAgent) => {
        console.log("Delete voice template:", template.id);
        // TODO: Implement delete functionality
    };

    const handleNewTemplate = () => {
        if (activeAgentTab === "chat") {
            navigate("/super-admin/templates/new");
        } else {
            navigate("/super-admin/voice-templates/new");
        }
    };

    const handleAgentNameClick = (template: TemplateAgent) => {
        if (organization && template.organization_id === organization.id) {
            navigate(`/agents/${template.id}`);
        }
    };

    const handleVoiceAgentNameClick = (template: VoiceTemplateAgent) => {
        if (organization && template.organization_id === organization.id) {
            navigate(`/voice-agents/${template.id}`);
        }
    };

    const isClickable = (template: TemplateAgent | VoiceTemplateAgent) => {
        return organization && template.organization_id === organization.id;
    };

    const renderTemplateTable = (templates: (TemplateAgent | VoiceTemplateAgent)[], isVoice: boolean = false) => {
        const currentTemplates = isVoice ? filteredVoiceTemplates : filteredTemplates;

    return (
                                    <div className="rounded-md border">
                                        <Table>
                                                                                    <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Organization</TableHead>
                                                <TableHead>Persona</TableHead>
                                                <TableHead>Industry</TableHead>
                                                <TableHead>Created</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                            <TableBody>
                        {currentTemplates.map((template) => (
                                                    <TableRow key={template.id}>
                                                        <TableCell className="font-medium">
                                                            <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                                            isVoice ? 'bg-green-100' : 'bg-primary/10'
                                        }`}>
                                            {isVoice ? (
                                                <Phone className="h-4 w-4 text-green-600" />
                                            ) : (
                                                <MessageSquare className="h-4 w-4 text-primary" />
                                            )}
                                                                </div>
                                                                <div>
                                                                    <div 
                                                                        className={`font-medium ${isClickable(template) ? 'text-primary cursor-pointer hover:underline flex items-center gap-1' : ''}`}
                                                onClick={() => isVoice ? handleVoiceAgentNameClick(template as VoiceTemplateAgent) : handleAgentNameClick(template as TemplateAgent)}
                                                                    >
                                                                        {template.title}
                                                                        {isClickable(template) && <ExternalLink className="h-3 w-3" />}
                                                                    </div>
                                                                    <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                                                                        {template.description || "No description"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {template.organizations ? (
                                                                <Badge variant="outline" className="bg-gray-50 text-gray-700">
                                                                    {template.organizations.name}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary">Unknown Organization</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {template.personas ? (
                                                                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                                                    👤 {template.personas.avatar_name}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary">No Persona</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {template.industries ? (
                                                                <Badge variant="outline">
                                                                    {template.industries.emoji} {template.industries.name}
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary">No Industry</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>{formatDate(template.created_at)}</TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {template.is_active ? (
                                                                    <Badge variant="outline" className="bg-green-100 text-green-800">
                                                                        <Check className="h-3 w-3 mr-1" /> Active
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                                                                        <X className="h-3 w-3 mr-1" /> Inactive
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                                                        <span className="sr-only">Open menu</span>
                                                                        <MoreHorizontal className="h-4 w-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => isVoice ? handleViewVoiceTemplate(template as VoiceTemplateAgent) : handleViewTemplate(template as TemplateAgent)}>
                                                                        <Eye className="h-4 w-4 mr-2" /> View
                                                                    </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => isVoice ? handleEditVoiceTemplate(template as VoiceTemplateAgent) : handleEditTemplate(template as TemplateAgent)}>
                                                                        <Pencil className="h-4 w-4 mr-2" /> Edit
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                onClick={() => isVoice ? handleDeleteVoiceTemplate(template as VoiceTemplateAgent) : handleDeleteTemplate(template as TemplateAgent)}
                                                                        className="text-red-600"
                                                                    >
                                                                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
        );
    };

    return (
        <MainLayout>
            <SuperAdminLayout
                title="Template Manager"
                description="Manage and organize agent templates for all organizations"
            >
                <Tabs defaultValue="templates">
                    <div className="flex justify-between items-center mb-6">
                        <TabsList>
                            <TabsTrigger value="templates">Templates</TabsTrigger>
                            <TabsTrigger value="industries">Industries</TabsTrigger>
                        </TabsList>

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                                Refresh
                            </Button>
                            <Button size="sm" onClick={handleNewTemplate}>
                                <Plus className="h-4 w-4 mr-2" />
                                New Template
                            </Button>
                        </div>
                    </div>

                    <TabsContent value="templates">
                        <Card>
                            <CardHeader>
                                <CardTitle>Agent Templates</CardTitle>
                                <CardDescription>
                                    Browse and manage templates that organizations can use to quickly create agents
                                </CardDescription>
                                <div className="relative w-full max-w-sm mt-4">
                                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        placeholder="Search templates..."
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Chat vs Voice Tabs */}
                                <Tabs value={activeAgentTab} onValueChange={setActiveAgentTab} className="w-full">
                                    <TabsList className="grid w-full grid-cols-2 mb-6">
                                        <TabsTrigger value="chat" className="flex items-center gap-2">
                                            <MessageSquare className="h-4 w-4" />
                                            Chat Templates ({filteredTemplates.length})
                                        </TabsTrigger>
                                        <TabsTrigger value="voice" className="flex items-center gap-2">
                                            <Phone className="h-4 w-4" />
                                            Voice Templates ({filteredVoiceTemplates.length})
                                        </TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="chat" className="mt-0">
                                        {isLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                                            </div>
                                        ) : filteredTemplates.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground">
                                                {searchTerm
                                                    ? `No chat templates found matching "${searchTerm}"`
                                                    : "No chat templates available"}
                                            </div>
                                        ) : (
                                            renderTemplateTable(filteredTemplates, false)
                                        )}
                                    </TabsContent>

                                    <TabsContent value="voice" className="mt-0">
                                        {isLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                                            </div>
                                        ) : filteredVoiceTemplates.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground">
                                                {searchTerm
                                                    ? `No voice templates found matching "${searchTerm}"`
                                                    : "No voice templates available"}
                                            </div>
                                        ) : (
                                            renderTemplateTable(filteredVoiceTemplates, true)
                                        )}
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="industries">
                        <Card>
                            <CardHeader>
                                <CardTitle>Template Industries</CardTitle>
                                <CardDescription>
                                    Overview of industries represented in agent templates
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {industries.map((industry) => {
                                        const chatCount = templates.filter(t => t.industries?.id === industry.id).length;
                                        const voiceCount = voiceTemplates.filter(t => t.industries?.id === industry.id).length;
                                        const totalCount = chatCount + voiceCount;
                                        
                                        return (
                                            <Card key={industry.id} className="overflow-hidden">
                                                <CardHeader className="pb-2">
                                                    <CardTitle className="text-lg flex items-center gap-2">
                                                        <span className="text-2xl">{industry.emoji}</span>
                                                        {industry.name}
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="space-y-2">
                                                    <p className="text-sm text-muted-foreground">
                                                            {totalCount} template{totalCount !== 1 ? 's' : ''} available
                                                        </p>
                                                        <div className="flex gap-4 text-xs">
                                                            <div className="flex items-center gap-1">
                                                                <MessageSquare className="h-3 w-3 text-primary" />
                                                                <span>{chatCount} Chat</span>
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <Phone className="h-3 w-3 text-green-600" />
                                                                <span>{voiceCount} Voice</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </SuperAdminLayout>
        </MainLayout>
    );
};

export default TemplateManagerPage; 